import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, UserPlus, Search, Loader2, Pencil, Trash2, Users, Link2, X,
} from "lucide-react";

const STATUS_META = {
  BOZZA: { label: "Bozza", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  VERIFICATA: { label: "Verificata", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  REVOCATA: { label: "Revocata", cls: "bg-red-100 text-red-700 border-red-200" },
};

const EMPTY_FORM = {
  nome: "", cognome: "", ragione_sociale: "", codice_fiscale: "",
  email: "", cellulare: "", anagrafica_id: null,
};

export default function SignIdentities() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [anaQuery, setAnaQuery] = useState("");
  const [anaResults, setAnaResults] = useState([]);
  const [anaSearching, setAnaSearching] = useState(false);

  const load = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/sign/identities", { params: { q: search } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setAnaQuery(""); setAnaResults([]);
    setDialogOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nome: it.nome || "", cognome: it.cognome || "", ragione_sociale: it.ragione_sociale || "",
      codice_fiscale: it.codice_fiscale || "", email: it.email || "", cellulare: it.cellulare || "",
      anagrafica_id: it.anagrafica_id || null,
    });
    setAnaQuery(""); setAnaResults([]);
    setDialogOpen(true);
  };

  const searchAnagrafica = async () => {
    if (!anaQuery.trim()) return;
    setAnaSearching(true);
    try {
      const { data } = await api.get("/sign/anagrafiche/search", { params: { q: anaQuery } });
      setAnaResults(data);
      if (!data.length) toast.info("Nessuna anagrafica trovata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAnaSearching(false);
    }
  };

  const pickAnagrafica = (a) => {
    setForm((f) => ({
      ...f,
      ragione_sociale: a.ragione_sociale || f.ragione_sociale,
      codice_fiscale: a.codice_fiscale || f.codice_fiscale,
      email: a.email || f.email,
      cellulare: a.cellulare || f.cellulare,
      nome: f.nome || a.ragione_sociale || "",
      anagrafica_id: a.anagrafica_id,
    }));
    setAnaResults([]);
    setAnaQuery("");
    toast.success("Anagrafica collegata");
  };

  const save = async () => {
    if (!form.nome.trim() && !form.ragione_sociale.trim()) {
      toast.error("Indicare almeno il nome o la ragione sociale");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sign/identities/${editing.identity_id}`, form);
        toast.success("Identity aggiornata");
      } else {
        await api.post("/sign/identities", form);
        toast.success("Identity creata");
      }
      setDialogOpen(false);
      load(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sign/identities/${deleteTarget.identity_id}`);
      toast.success("Identity eliminata");
      setDeleteTarget(null);
      load(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const displayName = (it) =>
    [it.nome, it.cognome].filter(Boolean).join(" ") || it.ragione_sociale || "—";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-identities-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => nav("/assietica-sign")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
          data-testid="sign-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Assietica Sign
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Clienti / Identity</h1>
              <p className="text-sm text-slate-500">Anagrafiche firmatarie del modulo Sign</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-new-identity-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Nuova identity
          </Button>
        </div>

        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)}
            placeholder="Cerca per nome, CF, e-mail, cellulare o ID…"
            className="pl-10 bg-white"
            data-testid="sign-identity-search"
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-400" data-testid="sign-identities-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="sign-identities-empty">
              Nessuna identity. Crea la prima con "Nuova identity".
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="sign-identities-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left font-medium px-4 py-3">ID</th>
                  <th className="text-left font-medium px-4 py-3">Nominativo</th>
                  <th className="text-left font-medium px-4 py-3">Codice fiscale</th>
                  <th className="text-left font-medium px-4 py-3">Contatti</th>
                  <th className="text-left font-medium px-4 py-3">Stato</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const sm = STATUS_META[it.status] || STATUS_META.BOZZA;
                  return (
                    <tr key={it.identity_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`sign-identity-row-${it.identity_id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{it.identity_id}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{displayName(it)}</div>
                        {it.ragione_sociale && (it.nome || it.cognome) && (
                          <div className="text-xs text-slate-400">{it.ragione_sociale}</div>
                        )}
                        {it.anagrafica_id && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-sky-600 mt-0.5">
                            <Link2 className="h-3 w-3" /> anagrafica collegata
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">{it.codice_fiscale || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{it.email || "—"}</div>
                        <div className="text-xs text-slate-400">{it.cellulare || ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={sm.cls}>{sm.label}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-sky-600"
                            onClick={() => openEdit(it)} data-testid={`sign-edit-${it.identity_id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500 hover:text-red-600"
                            onClick={() => setDeleteTarget(it)} data-testid={`sign-delete-${it.identity_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="sign-identity-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica identity" : "Nuova identity"}</DialogTitle>
          </DialogHeader>

          {!editing && (
            <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3 mb-1">
              <Label className="text-xs text-slate-500 mb-1.5 block">Collega da anagrafica condivisa (opzionale)</Label>
              <div className="flex gap-2">
                <Input
                  value={anaQuery}
                  onChange={(e) => setAnaQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchAnagrafica())}
                  placeholder="Cerca cliente per nome, CF, P.IVA…"
                  className="bg-white h-9"
                  data-testid="sign-ana-search-input"
                />
                <Button type="button" variant="outline" className="h-9" onClick={searchAnagrafica} disabled={anaSearching} data-testid="sign-ana-search-btn">
                  {anaSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
              {anaResults.length > 0 && (
                <div className="mt-2 max-h-40 overflow-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                  {anaResults.map((a) => (
                    <button key={a.anagrafica_id} type="button" onClick={() => pickAnagrafica(a)}
                      className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm" data-testid={`sign-ana-result-${a.anagrafica_id}`}>
                      <div className="font-medium text-slate-800">{a.ragione_sociale || "—"}</div>
                      <div className="text-xs text-slate-400">{[a.codice_fiscale, a.comune, a.email].filter(Boolean).join(" · ")}</div>
                    </button>
                  ))}
                </div>
              )}
              {form.anagrafica_id && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-sky-700">
                  <Link2 className="h-3.5 w-3.5" /> Anagrafica collegata
                  <button type="button" onClick={() => setForm((f) => ({ ...f, anagrafica_id: null }))} className="text-slate-400 hover:text-red-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="sign-form-nome" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Cognome</Label>
              <Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="sign-form-cognome" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Ragione sociale (azienda)</Label>
              <Input value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} data-testid="sign-form-ragione" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Codice fiscale</Label>
              <Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} data-testid="sign-form-cf" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">E-mail</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sign-form-email" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Cellulare</Label>
              <Input value={form.cellulare} onChange={(e) => setForm({ ...form, cellulare: e.target.value })} data-testid="sign-form-cellulare" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sign-form-cancel">Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-form-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {editing ? "Salva modifiche" : "Crea identity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="sign-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'identity?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && <>Verrà eliminata definitivamente <strong>{displayName(deleteTarget)}</strong> ({deleteTarget.identity_id}). L'operazione non è reversibile.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="sign-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
